import React, { useContext, useState, useEffect, Fragment } from 'react';
import classNames from 'classnames';

import BaseCarousel from 'b2b-ui-components-lib/lib/components/baseCarousel';
import Button from 'b2b-ui-components-lib/lib/components/button';

import { AppContext } from 'App';

import { CarouselDefaultSetting } from '../../../constant';
import {
  getBandwithValueInMbps,
  getFromSessionStorage,
  getImageSrc,
  getb2cSessionData,
  handleUnauthAddDiConnections,
  setInSessionStorage,
  trackAnalytics
} from 'common/utils/app.utils';
import { getCurrentLead } from 'tokenGeneration/lead.util';
import { FEASIBILITY_RESPONSE, MODULE_ID } from 'home/common/constants/constants';
import { LEAD_ID } from 'home/view/manage-feasibility/constants';

import './PlansCarouselSlide.scss';
import { trackGTM1, trackGTMSelectedPlan } from 'app-layout/utils';

const PlansCarouselSlide = (props) => {
  const { location, showLoader, showNotification, setLeadId, updateActiveIndex, userFilledPincode } = props;

  const [plans, setPlans] = useState([]);

  const leadData = getCurrentLead?.();

  const feasibilityData = getFromSessionStorage(MODULE_ID, FEASIBILITY_RESPONSE);
  const selectedBandwidth = location?.state?.bandwidth;

  const { pageData } = useContext(AppContext);
  const { list } = pageData?.plans;

  const handleSelectPlan = (planItem) => {
    setPlans((plans) => {
      return plans?.map((item) => {
        if (getBandwithValueInMbps(item?.bandwidth) === getBandwithValueInMbps(planItem?.bandwidth)) {
          item['selected'] = true;
          item['cta_name'] = 'Selected Plan';
        } else {
          item['selected'] = false;
          item['cta_name'] = 'Select Plan';
        }

        return item;
      });
    });
  };

  const getSelectedBandwidth = () => {
    const selectedPlan = plans?.find((item) => item?.selected);
    return `${getBandwithValueInMbps(selectedPlan?.bandwidth)} Mbps`;
  };

  const transformSelectedPlanToPayload = () => {
    const { addressReducer } = getb2cSessionData();
    const { pincode } = JSON.parse(addressReducer);

    return [
      {
        planDetail: {
          planPeriod: 12,
          speed: getSelectedBandwidth(),
          service: 'UNMANAGED'
        },
        feasibilityId: feasibilityData?.feasibilityId,
        feasibilityStatus: feasibilityData?.feasibilityStatus,
        contactType: leadData?.authorizedSignatory?.contactType,
        firstName: leadData?.authorizedSignatory?.firstName,
        lastName: leadData?.authorizedSignatory?.lastName,
        companyName: leadData?.authorizedSignatory?.companyName,
        email: leadData?.authorizedSignatory?.email,
        phoneNumber: leadData?.authorizedSignatory?.phoneNumber,
        authorisedPerson: leadData?.authorizedSignatory?.authorisedPerson,
        address: {
          addressType: 'COMPANY',
          pincode: userFilledPincode ?? pincode,
          country: feasibilityData?.address?.country ?? 'India',
          // state: feasibilityData?.address?.state,
          city: feasibilityData?.address?.city,
          addressLine1: feasibilityData?.address?.addressString,
          circle: leadData?.authorizedSignatory?.address?.circle,
          serviceable: leadData?.authorizedSignatory?.address?.serviceable
        },
        poiMatchCount: leadData?.authorizedSignatory?.poiMatchCount,
        poiDedupeStatus: leadData?.authorizedSignatory?.poiDedupeStatus,
        installationPincode: userFilledPincode ?? pincode
      }
    ];
  };

  const handleOrderSubmit = () => {
    trackAnalytics({
      eVar9: 'b2b| dedicated internet| home page| feasibility| location access| address details| order summary| submit request',
      prop0: 'b2b| dedicated internet',
      prop14: leadData?.authorizedSignatory?.phoneNumber,
      prop35: leadData?.authorizedSignatory?.companyName,
      prop15: leadData?.authorizedSignatory?.email,
      prop17: leadData?.authorizedSignatory?.firstName,
      prop19: userFilledPincode,
      prop20: feasibilityData?.address?.addressString,
      prop21: getSelectedBandwidth(),
      eVar6: 'ql_lead_created'
    });

    const payload = transformSelectedPlanToPayload();

    const successCb = (diConnectionRes) => {
      const leadId = diConnectionRes?.data?.result?.[0]?.leadId;
      setInSessionStorage(MODULE_ID, LEAD_ID, leadId);
      setLeadId(leadId, (_prevValue, newValue) => {
        if (newValue) {
          trackAnalytics({
            eVar0: 'b2b| dedicated internet| home page| feasibility| location access| address details| order summary| thank you',
            prop0: 'b2b| dedicated internet',
            prop14: leadData?.authorizedSignatory?.phoneNumber,
            prop35: leadData?.authorizedSignatory?.companyName,
            prop15: leadData?.authorizedSignatory?.email,
            prop17: leadData?.authorizedSignatory?.firstName,
            prop19: userFilledPincode,
            prop20: feasibilityData?.address?.addressString,
            prop21: getSelectedBandwidth(),
            eVar6: 'ql_lead_created'
          });
          trackGTMSelectedPlan(feasibilityData?.feasibilityConfidence, getSelectedBandwidth());
          updateActiveIndex(4);
        }
      });
    };

    handleUnauthAddDiConnections(payload, successCb, { setLoader: showLoader, showNotification });
  };

  const getCarouselSetting = () => {
    return {
      ...CarouselDefaultSetting,
      slidesToShow: plans?.length ?? 5,
      slidesToScroll: plans?.length ?? 5
    };
  };

  useEffect(() => {
    const listCopy = [...list];

    const filteredList = listCopy?.filter((item) => {
      const bandwidthMbps = getBandwithValueInMbps(item?.bandwidth);

      if (feasibilityData?.maxBandwidth) {
        const maxFeasibleBw = feasibilityData?.maxBandwidth === 1000 ? 1024 : feasibilityData?.maxBandwidth; // BE sets 1000 Mbps which is 1Gbps (1024 Mbps)

        return bandwidthMbps <= maxFeasibleBw;
      }

      return bandwidthMbps <= 1024; // IF maxBandwidth:0, return all default plans (till 1 Gbps)
    });

    const currentPlans = filteredList?.map((item) => {
      const getSelectedBw = () => {
        let selectedBw = null;

        // feasibilityData?.requestedBandwidth returns 2 by default (not-valid scenario for unauth journey); if not user selected bandwidth, set 10Mbps as default selected bandwidth on order summary page.
        if (feasibilityData?.requestedBandwidth && feasibilityData?.requestedBandwidth !== 2) {
          selectedBw = `${feasibilityData?.requestedBandwidth}Mbps`;

          // handling 1Gbps
          if (feasibilityData.requestedBandwidth === 1000) {
            selectedBw = '1Gbps';
          }
        } else if (selectedBandwidth) {
          selectedBw = selectedBandwidth;
        }

        return selectedBw;
      };

      const customerSelectedBw = getSelectedBw() ?? filteredList?.[0]?.bandwidth;
      const isBwSelected = item?.bandwidth === customerSelectedBw;

      item['image'] = getImageSrc(item);
      item['selected'] = isBwSelected;
      item['cta_name'] = isBwSelected ? 'Selected Plan' : 'Select Plan';

      return item;
    });

    setPlans(currentPlans);
  }, []);

  return (
    <Fragment>
      <div className="order_summary_plans">
        <div className="plan_carosuel_controller">
          <div className="container">
            <h3>Selected plan</h3>
            {plans && plans.length > 0 && (
              <BaseCarousel {...getCarouselSetting()}>
                {plans?.map((item, index) => (
                  <div
                    className={classNames({
                      plan_box: true,
                      current_plan: item?.selected
                    })}
                    key={index}
                  >
                    {/*{item?.recommended && <div className="recommended_tag">{item?.recommended_text}</div> }*/}
                    <div className="plan_head">{item?.bandwidth}</div>
                    <div className="plan_body">
                      <div className="plan_price">
                        <span className="price_text">{item?.title}</span>
                      </div>
                      <div className="progress_box">
                        <img src={item?.image} alt="Plan Image" />
                      </div>
                      <div className="plan_desc">{item?.description}</div>
                      <div className="features">
                        <ul className="features_list">
                          {item?.features?.map((featureItem, featureIndex) => (
                            <li key={featureIndex}>
                              {featureItem?.title}
                              <span>{featureItem?.subTitle}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                    <div className="plan_action">
                      <Button displayName={item?.cta_name} variant={item?.selected ? 'backgroundbtn' : 'outlinebtn'} onClick={() => handleSelectPlan(item)} />
                    </div>
                  </div>
                ))}
              </BaseCarousel>
            )}
          </div>
        </div>
      </div>
      <div className="submit_request">
        <div className="container">
          <Button displayName="Submit request" onClick={handleOrderSubmit} />
        </div>
      </div>
    </Fragment>
  );
};

export default PlansCarouselSlide;
